<script lang="ts">
	import type { CarEnergy$options } from '$houdini';

	import type { SearchCarResult } from '$lib/components/search/cars/types';
	import type { ClassValue } from 'svelte/elements';
	import AlertBox from '$lib/components/atoms/alertBox/AlertBox.svelte';
	import Select from '$lib/components/atoms/select/Select.svelte';
	import Spinner from '$lib/components/atoms/spinner/Spinner.svelte';
	import CarSearch from '$lib/components/search/cars/CarSearch.svelte';
	import LogInToRegisterCar from '$lib/components/search/cars/LogInToRegisterCar.svelte';
	import SaveCarButton from '$lib/components/search/cars/SaveCarButton.svelte';
	import * as c from '$lib/i18n/computedMessages';
	import { track } from '$lib/services/tracking/googleAnalytics';
	import * as m from '$paraglide/messages';
	import { watch } from 'runed';

	interface Props {
		selectedCar: SearchCarResult | null;
		isAuthenticated: boolean;
		saveCarLoading: boolean;
		initialStateLoading: boolean;
		userCarId?: string;
		class?: ClassValue;
		onSaveCar?: (carId: string | undefined) => void;
	}

	let {
		selectedCar = $bindable(),
		isAuthenticated,
		saveCarLoading,
		initialStateLoading,
		userCarId,
		class: classes,
		onSaveCar,
	}: Props = $props();

	let selectedFuel: string | undefined = $state();
	let selectedConstructionYears: string | undefined = $state();
	let selectedMotor: string | undefined = $state();

	function getFilteredCarOptions(
		selectedCar: SearchCarResult | null,
		selectedFuel: string | undefined,
		selectedConstructionYears: string | undefined,
	) {
		if (!selectedCar) {
			return [];
		}

		return selectedCar.options.filter(
			option =>
				(!selectedFuel || option.energy === selectedFuel)
					&& (!selectedConstructionYears || option.constructionYears === selectedConstructionYears),
		);
	}

	function getFuelOptions(selectedCar: SearchCarResult | null) {
		const fuelSet = selectedCar
			? new Set(selectedCar?.options.map(option => option.energy))
			: new Set<CarEnergy$options>();

		return Array.from(fuelSet).map(option => ({
			value: option,
			label: c.carEnergies(option),
		}));
	}

	const getConstructionYearsArray = (constructionYears: string) =>
		constructionYears.replace(/\s/g, '').split('-');

	function sortConstructionYears(a: string, b: string) {
		const [startYearA, _] = getConstructionYearsArray(a);
		const [startYearB, __] = getConstructionYearsArray(b);

		return Number.parseInt(startYearA, 10) - Number.parseInt(startYearB, 10);
	}

	function getConstructionYearsOptions(filteredOptions: SearchCarResult['options']) {
		const constructionYearSet = new Set(filteredOptions.map(option => option.constructionYears));
		return Array.from(constructionYearSet)
			.sort(sortConstructionYears)
			.map(option => ({
				value: option,
				label: option,
			}));
	}

	function getMotorOptions(filteredOptions: SearchCarResult['options']) {
		const motorSet = new Set(
			filteredOptions.map(option => option.motor).filter(value => value !== ''),
		);
		return Array.from(motorSet).map(option => ({
			value: option,
			label: option,
		}));
	}

	function resetSelectedFuel() {
		selectedFuel = '';
	}
	function resetSelectedConstructionYears() {
		selectedConstructionYears = '';
	}
	function resetSelectedMotor() {
		selectedMotor = '';
	}

	const fuelOptions = $derived(getFuelOptions(selectedCar));
	// Do not take into account selectedConstructionYears because it should only depends on car and fuel
	const constructionYearOptions = $derived(getConstructionYearsOptions(
		getFilteredCarOptions(selectedCar, selectedFuel, undefined),
	));
	const motorOptions = $derived(getMotorOptions(
		getFilteredCarOptions(selectedCar, selectedFuel, selectedConstructionYears),
	));

	function computeDefaultValues(userCarId: string | undefined) {
		if (!selectedCar) {
			return;
		}

		const userCarOption = selectedCar.options.find(option => option.carId === userCarId);

		selectedFuel = userCarOption?.energy;
		selectedConstructionYears = userCarOption?.constructionYears;
		selectedMotor = userCarOption?.motor;
	}

	watch(() => userCarId, computeDefaultValues);

	const selectedCarId = $derived(selectedCar?.options.find(
		option =>
			option.constructionYears === selectedConstructionYears
				&& option.energy === selectedFuel
				&& option.motor === selectedMotor,
	)?.carId);

	const shouldDisplayMotorSelect = $derived(!selectedCar || motorOptions.length > 0);
	const isSaveCarButtonDisabled = $derived(shouldDisplayMotorSelect
		? !selectedMotor
		: !selectedConstructionYears);
</script>

<div class={['flex flex-col gap-4 w-full', classes]}>
	{#if initialStateLoading}
		<div class="flex items-center justify-center"><Spinner /></div>
	{:else}
		<AlertBox
			color="amber"
			icon="construction-cone"
			title={m.register__car__info_box__title()}
			text={m.register__car__info_box__text()}
		/>
		<CarSearch
			onSelect={(result) => {
				// Make sure not to deselect anything if user clicks on the same car that was selected
				if (result?.fullname !== selectedCar?.fullname) {
					resetSelectedFuel();
					resetSelectedConstructionYears();
					resetSelectedMotor();
				}
				selectedCar = result;
				track({
					action: 'searchbar_vehicle',
					payload: {
						action: 'selection',
						car_brand: selectedCar?.fullname,
					},
				});
			}}
			onActivate={() => {
				track({
					action: 'searchbar_vehicle',
					payload: {
						action: 'click',
					},
				});
			}}
			initialValue={selectedCar?.fullname || ''}
		/>
		<Select
			label={m.register__car__fuel()}
			options={fuelOptions}
			ariaLabel={m.register__car__fuel()}
			disabled={!selectedCar}
			defaultValue={selectedFuel}
			dropDownSameWidth
			onSelectedChange={(fuel) => {
				resetSelectedConstructionYears();
				resetSelectedMotor();
				selectedFuel = fuel;
				track({
					action: 'searchbar_fuel',
					payload: {
						action: 'selection',
						car_name: selectedCar?.fullname,
						car_gastype: selectedFuel,
					},
				});
			}}
			onOpenChange={() => {
				track({
					action: 'searchbar_fuel',
					payload: {
						action: 'click',
					},
				});
			}}
		/>
		<Select
			label={m.register__car__construction_year()}
			options={constructionYearOptions}
			ariaLabel={m.register__car__construction_year()}
			defaultValue={selectedConstructionYears}
			dropDownSameWidth
			onSelectedChange={(year) => {
				resetSelectedMotor();
				selectedConstructionYears = year;
				track({
					action: 'searchbar_year',
					payload: {
						action: 'selection',
						car_name: selectedCar?.fullname,
						car_gastype: selectedFuel,
						car_year: selectedConstructionYears,
					},
				});
			}}
			onOpenChange={() => {
				track({
					action: 'searchbar_year',
					payload: {
						action: 'click',
					},
				});
			}}
		/>
		{#if shouldDisplayMotorSelect}
			<Select
				label={m.register__car__motor()}
				options={motorOptions}
				ariaLabel={m.register__car__motor()}
				defaultValue={selectedMotor}
				dropDownSameWidth
				onSelectedChange={(motor) => {
					selectedMotor = motor;
					track({
						action: 'searchbar_motorization',
						payload: {
							action: 'selection',
							car_name: selectedCar?.fullname,
							car_gastype: selectedFuel,
							car_year: selectedConstructionYears,
							car_motorization: selectedMotor,
						},
					});
				}}
				onOpenChange={() => {
					track({
						action: 'searchbar_motorization',
						payload: {
							action: 'click',
						},
					});
				}}
			/>
		{/if}
		{#if !isAuthenticated}
			<LogInToRegisterCar />
		{:else}
			<SaveCarButton
				disabled={isSaveCarButtonDisabled}
				onclick={() => onSaveCar?.(selectedCarId)}
				isLoading={saveCarLoading}
			/>
		{/if}
	{/if}
</div>
